import {Box, Button, HStack, Progress, Text} from '@chakra-ui/react';
import {
  TablerAlertTriangle,
  TablerCircleCheck,
  TablerInfoCircle,
  TablerSearch,
} from '../../icons';

// Slim inline banner meant to sit directly under the picker's search input.
// Unlike `EhrDeepSearchStatus` (which is a full-size empty-state panel),
// this one stays visible even when the cached search has returned hits —
// so users can tell a deep EHR search is running in the background and
// can manually trigger one when the cached list is hiding the patient
// they actually want (the "other Lucas" case).
//
// Render states (highest priority first):
//   1. in-flight              → animated "Checking full EHR…" with progress bar
//   2. error                  → red "Full EHR search failed — try again" chip
//                                with a retry button
//   3. unavailable (503)      → muted "Deep search unavailable…" chip
//   4. completion (success)   → persistent "Checked full EHR — N more
//                                match(es)" / "no new matches" / "no
//                                matches found" chip. Persists until the
//                                user kicks off another deep search or
//                                changes the query out of cache range —
//                                fading after a few seconds was making
//                                folks think the search hadn't run.
//   5. idle + manual trigger  → a "Search full EHR by patient name"
//                                affordance. Render shape depends on
//                                `promoted`:
//                                  - false (default): tiny inline link
//                                  - true: full-width outline button,
//                                    used when cache results are 0 or
//                                    near-zero so the action is unmissable
//   6. otherwise              → null (keeps the picker header compact)
export default function EhrDeepSearchBanner({
  isDeepSearching = false,
  deepSearchSlow = false,
  deepSearchUnavailable = false,
  // Error object emitted by the hook when the live EHR call fails. We
  // surface this prominently so users don't end up staring at "no
  // matches" wondering whether the network round-trip even happened.
  deepSearchError = null,
  canTriggerManualDeepSearch = false,
  onTriggerDeepSearch,
  // Optional { at, totalCount, additionalCount } snapshot emitted by the
  // hook when a deep search completes. We render a persistent
  // confirmation chip so users know the network call actually ran —
  // especially important when the deep result set fully overlapped with
  // what was already visible from cache.
  deepSearchCompletion = null,
  // Render the idle "Search full EHR" affordance even when there are no
  // local results yet. Pickers should set this true once the query is
  // long enough to be a reasonable deep-search candidate, and may
  // additionally pass `promoted` to upgrade the visual weight when the
  // cache returned nothing.
  showManualTriggerWhenIdle = false,
  // Promote the manual-trigger CTA to a full-width prominent button.
  // Use this when cache results are 0 or near-zero so the user can
  // clearly see the path forward instead of staring at "no matches".
  promoted = false,
  // When the picker is rendering an `EhrDeepSearchStatus` panel below us
  // (typically because the cached result list is empty), the panel
  // already owns the in-flight progress bar and the "deep search
  // unavailable" copy. Suppressing those branches here avoids the
  // duplicate-progress-bar / duplicate-copy pile-up that would otherwise
  // show up while still letting the banner render the promoted CTA,
  // error chip, and completion chip — states the panel doesn't cover.
  suppressInFlightAndUnavailable = false,
}: Record<string, any>) {
  const subtle = 'gray.500';

  if (isDeepSearching && !suppressInFlightAndUnavailable) {
    return (
      <Box px={1} pt={1} pb={2} w="100%">
        <HStack spacing={2} align="center" mb={1}>
          <Box
            w="6px"
            h="6px"
            borderRadius="full"
            bg="opalite.500"
            sx={{
              animation: 'deepSearchPulseDot 1.2s ease-in-out infinite',
              '@keyframes deepSearchPulseDot': {
                '0%, 100%': {opacity: 0.35},
                '50%': {opacity: 1},
              },
            }}
          />
          <Text color={subtle} fontSize="xs" fontWeight="normal">
            {deepSearchSlow
              ? 'Still checking full EHR — this can take up to ~15s.'
              : 'Checking full EHR for more matches…'}
          </Text>
        </HStack>
        <Progress
          size="xs"
          w="100%"
          isIndeterminate
          colorScheme="opalite"
          borderRadius="full"
        />
      </Box>
    );
  }

  // Idle state: we may have a manual trigger affordance that should
  // render alongside completion / error chips.
  const canRenderManual =
    showManualTriggerWhenIdle &&
    canTriggerManualDeepSearch &&
    !!onTriggerDeepSearch;

  const manualTriggerLink = canRenderManual ? (
    <Button
      variant="link"
      size="xs"
      colorScheme="opalite"
      leftIcon={<TablerSearch />}
      onClick={onTriggerDeepSearch}
    >
      Search full EHR by patient name
    </Button>
  ) : null;

  if (deepSearchError) {
    // The hook already cleared spinners/results; surface the failure
    // explicitly so users don't think "no matches" means the request
    // succeeded — or worse, that the patient doesn't exist.
    const isTimeout = deepSearchError?.name === 'TimeoutError';
    const status = deepSearchError?.status;
    // 403 is the one genuinely non-transient case (EHR not configured for
    // this org); show its message verbatim. Everything else — a 5xx from a
    // down EHR integration (the Zatanna-login-401 outage surfaces as a
    // backend 502), a 429, or a bare network error — is transient: the
    // patient may well exist, the live lookup just couldn't run right now.
    const isConfigError = status === 403;
    const isTemporarilyUnavailable = !isTimeout && !isConfigError;

    let errorCopy;
    if (isTimeout) {
      errorCopy = 'Full EHR search timed out — no patients fetched.';
    } else if (isConfigError) {
      errorCopy =
        deepSearchError?.message ||
        'Full EHR search is unavailable for your organization.';
    } else {
      errorCopy =
        'Full EHR search is temporarily unavailable — please try again shortly.';
    }
    const tone = isTemporarilyUnavailable ? 'gray.600' : 'gray.600';
    return (
      <HStack
        spacing={2}
        px={1}
        pt={1}
        pb={2}
        w="100%"
        color={tone}
        fontSize="xs"
        align="center"
      >
        <Box display="inline-flex" alignItems="center">
          <TablerAlertTriangle size={12} />
        </Box>
        <Text>{errorCopy}</Text>
        {!isConfigError && canTriggerManualDeepSearch && onTriggerDeepSearch ? (
          <>
            <Text color={subtle}>·</Text>
            <Button
              variant="link"
              size="xs"
              colorScheme="gray"
              leftIcon={<TablerSearch />}
              onClick={onTriggerDeepSearch}
            >
              Try again
            </Button>
          </>
        ) : null}
      </HStack>
    );
  }

  if (deepSearchUnavailable && !suppressInFlightAndUnavailable) {
    return (
      <Box px={1} pt={1} pb={2} w="100%">
        <HStack spacing={2} align="center">
          <Box w="6px" h="6px" borderRadius="full" bg="gray.400" />
          <Text color={subtle} fontSize="xs">
            Deep EHR search unavailable (weekdays 7am–8pm ET).
          </Text>
        </HStack>
      </Box>
    );
  }

  if (deepSearchCompletion) {
    const total =
      typeof deepSearchCompletion.totalCount === 'number'
        ? deepSearchCompletion.totalCount
        : null;
    const additional =
      typeof deepSearchCompletion.additionalCount === 'number'
        ? deepSearchCompletion.additionalCount
        : null;

    let summary;
    if (total === 0) {
      summary = 'Checked full EHR — no matches found.';
    } else if (additional === null) {
      summary = `Checked full EHR — ${total} match${total === 1 ? '' : 'es'}.`;
    } else if (additional === 0) {
      summary = `Checked full EHR — no new matches (${total} already shown).`;
    } else {
      summary = `Checked full EHR — ${additional} more match${additional === 1 ? '' : 'es'}.`;
    }

    const isEmpty = total === 0;
    const Icon = isEmpty ? TablerInfoCircle : TablerCircleCheck;
    const iconColor = isEmpty ? 'gray.500' : 'gray.500';

    return (
      <HStack
        spacing={2}
        px={1}
        pt={1}
        pb={2}
        w="100%"
        color={subtle}
        fontSize="xs"
      >
        <Box color={iconColor} display="inline-flex" alignItems="center">
          <Icon size={12} />
        </Box>
        <Text>{summary}</Text>
        {manualTriggerLink ? (
          <>
            <Text>·</Text>
            {manualTriggerLink}
          </>
        ) : null}
      </HStack>
    );
  }

  if (canRenderManual && promoted) {
    // Promoted variant: full-width outline button. Used when cache results
    // are 0/near-zero so the user can see the next step without scanning
    // for a tiny link.
    return (
      <Box px={1} pt={1} pb={2} w="100%">
        <Button
          w="100%"
          size="sm"
          variant="outline"
          colorScheme="opalite"
          leftIcon={<TablerSearch />}
          onClick={onTriggerDeepSearch}
        >
          Search full EHR by patient name
        </Button>
        <Text fontSize="xs" color={subtle} textAlign="center" pt={1.5}>
          Searches by last name. Use “Last, First” for an exact match (e.g.
          Smith, Jane).
        </Text>
      </Box>
    );
  }

  if (manualTriggerLink) {
    return (
      <HStack
        spacing={1.5}
        px={1}
        pt={1}
        pb={2}
        w="100%"
        color={subtle}
        fontSize="xs"
      >
        <Text>Not who you&apos;re looking for?</Text>
        {manualTriggerLink}
      </HStack>
    );
  }

  return null;
}
